import { Badge } from '@/components/ui/badge'

interface JobStatusBadgeProps {
  status: 'queued' | 'running' | 'success' | 'completed' | 'failed'
  queuePosition?: number
  periodsRunning?: number
  periodsCompleted?: number
  className?: string
}

/**
 * Status badge component for job statuses in monitoring dashboard.
 *
 * Color coding:
 * - queued: yellow (shows queue position if available)
 * - running: blue with pulse animation (shows active periods if available)
 * - success/completed: green
 * - failed: red
 */
export function JobStatusBadge({ status, queuePosition, periodsRunning, periodsCompleted, className }: JobStatusBadgeProps) {
  const variants: Record<string, { variant: 'outline', className: string }> = {
    queued: {
      variant: 'outline' as const,
      className: 'border-yellow-500 text-yellow-700 bg-yellow-50 dark:bg-yellow-950 dark:text-yellow-300'
    },
    running: {
      variant: 'outline' as const,
      className: 'border-blue-500 text-blue-700 bg-blue-50 dark:bg-blue-950 dark:text-blue-300 animate-pulse'
    },
    success: {
      variant: 'outline' as const,
      className: 'border-green-500 text-green-700 bg-green-50 dark:bg-green-950 dark:text-green-300'
    },
    completed: {
      variant: 'outline' as const,
      className: 'border-green-500 text-green-700 bg-green-50 dark:bg-green-950 dark:text-green-300'
    },
    failed: {
      variant: 'outline' as const,
      className: 'border-red-500 text-red-700 bg-red-50 dark:bg-red-950 dark:text-red-300'
    }
  }

  // Determine effective status: "running" with 0 periods running AND 0 periods completed is actually "pending"
  // A job with completed periods but 0 running is still "running" (just between period processing)
  const isPendingDispatch = status === 'running' &&
    periodsRunning !== undefined && periodsRunning === 0 &&
    (periodsCompleted === undefined || periodsCompleted === 0)
  const effectiveStatus = isPendingDispatch ? 'pending' : status

  // Add pending variant (yellow like queued)
  const allVariants: Record<string, { variant: 'outline', className: string }> = {
    ...variants,
    pending: {
      variant: 'outline' as const,
      className: 'border-yellow-500 text-yellow-700 bg-yellow-50 dark:bg-yellow-950 dark:text-yellow-300'
    }
  }

  const config = allVariants[effectiveStatus] || variants.queued // fallback to queued style if unknown status

  // Build display text with context
  let displayText = effectiveStatus.charAt(0).toUpperCase() + effectiveStatus.slice(1)

  if (status === 'queued' && queuePosition && queuePosition > 0) {
    // Format large numbers with K suffix
    const formattedPosition = queuePosition >= 1000
      ? `${(queuePosition / 1000).toFixed(1)}K`
      : queuePosition.toString()
    displayText = `Queued (#${formattedPosition})`
  } else if (status === 'running' && periodsRunning !== undefined && periodsRunning > 0) {
    displayText = `Running (${periodsRunning} active)`
  }

  return (
    <Badge
      variant={config.variant}
      className={`${config.className} ${className || ''}`}
    >
      {displayText}
    </Badge>
  )
}
